import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Card } from "@/components/ui/card";
import { Clock } from "lucide-react";

export default function TimeTrackingWidget() {
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ["currentUser"],
    queryFn: () => base44.auth.me(),
  });

  const { data: timeEntries = [], isLoading: entriesLoading } = useQuery({
    queryKey: ["allTimeEntries"],
    queryFn: () => base44.entities.TimeEntry.list(),
  });

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list(),
  });

  const isLoading = userLoading || entriesLoading || tasksLoading;

  // Beregn total fakturerbar tid så langt
  const billableMinutes = timeEntries
    .filter(e => e.billable && e.duration_minutes)
    .reduce((sum, entry) => sum + (entry.duration_minutes || 0), 0);
  
  const billableHours = (billableMinutes / 60).toFixed(1);

  // Beregn estimert gjenstående tid fra oppgaver
  const incompleteTasks = tasks.filter(t => t.status !== "completed" && t.status !== "cancelled");
  const estimatedHoursRemaining = incompleteTasks.reduce((sum, task) => {
    if (!task.estimated_hours) return sum;
    const taskId = task.id;
    const actualHours = timeEntries
      .filter(e => e.task_id === taskId)
      .reduce((s, e) => s + (e.duration_minutes || 0) / 60, 0);
    const remaining = Math.max(0, task.estimated_hours - actualHours);
    return sum + remaining;
  }, 0);

  const HOURLY_RATE = 2500;
  const completedRevenue = parseFloat(billableHours) * HOURLY_RATE;
  const estimatedRevenue = estimatedHoursRemaining * HOURLY_RATE;
  const totalExpectedRevenue = completedRevenue + estimatedRevenue;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
            <Clock className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm text-slate-600">Total forventet omsetning</p>
            <p className="text-xs text-slate-500">Basert på tid</p>
          </div>
        </div>
        {isLoading ? (
          <div className="h-16 bg-white/50 rounded-lg animate-pulse" />
        ) : (
          <>
            <p className="text-4xl font-bold text-emerald-700 mb-2">
              {totalExpectedRevenue.toLocaleString()} kr
            </p>
            <p className="text-sm text-slate-600">
              {(parseFloat(billableHours) + estimatedHoursRemaining).toFixed(1)} timer totalt
            </p>
          </>
        )}
      </Card>

      <Card className="p-6 bg-white border-0 shadow-sm">
        {isLoading ? (
          <div className="space-y-3 animate-pulse">
            <div className="h-20 bg-slate-100 rounded-lg" />
            <div className="h-20 bg-slate-100 rounded-lg" />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Fakturert så langt</p>
              <p className="text-2xl font-bold text-slate-900">{completedRevenue.toLocaleString()} kr</p>
              <p className="text-xs text-slate-500 mt-1">{billableHours}t registrert</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-slate-500 mb-1">Estimert gjenstående</p>
              <p className="text-2xl font-bold text-amber-600">{estimatedRevenue.toLocaleString()} kr</p>
              <p className="text-xs text-slate-500 mt-1">{estimatedHoursRemaining.toFixed(1)}t estimert</p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}